import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useApp } from '@/lib/store';
import { VEHICLE_LABELS, timeAgo } from '@/lib/format';
import { Crown, ShieldCheck, MapPin, Clock, AlertTriangle, CheckCircle2, Users, RefreshCw, ArrowRight, Copy, Share2, Trash2, UserPlus } from 'lucide-react';

export default function FamilyDashboard() {
  const navigate = useNavigate();
  const t = useT();
  const user = useApp((s) => s.user);
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    api.familyDashboard()
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e?.response?.status === 403 ? 'not_family' : 'error'))
      .finally(() => setLoading(false));
    api.familyInvite().then(setInvite).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const r = setInterval(load, 15000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(r); clearInterval(c); };
  }, [load]);

  const copyLink = async () => {
    if (!inviteUrl) return;
    try { await navigator.clipboard.writeText(inviteUrl); toast.success(t('fam_copied')); }
    catch { toast.error(t('fam_err')); }
  };

  const shareLink = async () => {
    if (!inviteUrl) return;
    if (navigator.share) { try { await navigator.share({ title: 'KilalaRide Family', url: inviteUrl }); } catch (e) { console.debug('share sheet dismissed:', e); } }
    else copyLink();
  };

  const resetLink = async () => {
    try { const res = await api.familyInviteRegenerate(); setInvite((v) => ({ ...v, ...res })); toast.success(t('fam_regen_done')); }
    catch { toast.error(t('fam_err')); }
  };

  const removeMember = async (id) => {
    try { await api.familyRemoveMember(id); toast.success(t('fam_removed')); load(); }
    catch { toast.error(t('fam_err')); }
  };

  if (!loading && error === 'not_family') {
    return (
      <AppShell>
        <PageHeader title={t('fam_title')} subtitle={t('fam_sub')} />
        <div className="rounded-2xl p-6 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="family-upsell">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: 'color-mix(in srgb, var(--primary) 14%, transparent)' }}><Crown size={28} style={{ color: 'var(--primary)' }} /></div>
          <h2 className="font-bold text-lg" style={{ color: 'var(--text)' }}>{t('fam_locked_t')}</h2>
          <p className="text-sm mt-1 mb-4" style={{ color: 'var(--text-muted)' }}>{t('fam_locked_b')}</p>
          <button onClick={() => navigate('/app/account')} className="inline-flex items-center gap-2 rounded-full font-bold min-h-[48px] px-6" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="family-upgrade-cta">
            {t('fam_upgrade')} <ArrowRight size={18} />
          </button>
        </div>
      </AppShell>
    );
  }

  const members = data?.members || [];
  const active = members.filter((m) => m.status === 'active');
  const others = members.filter((m) => m.status !== 'active');
  const inviteUrl = invite?.token ? `${window.location.origin}/family/join/${invite.token}` : '';

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-1">
        <PageHeader title={t('fam_title')} subtitle={t('fam_sub')} />
        <button onClick={load} aria-label="Refresh" className="w-11 h-11 rounded-full flex items-center justify-center" style={{ color: 'var(--primary)' }} data-testid="family-refresh"><RefreshCw size={18} /></button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-2xl p-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}><Users size={15} /> {t('fam_active')}</div>
          <div className="text-3xl font-extrabold mt-1" style={{ color: 'var(--primary)' }} data-testid="family-active-count">{active.length}</div>
        </div>
        <div className="rounded-2xl p-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}><ShieldCheck size={15} /> {t('fam_watching')}</div>
          <div className="text-3xl font-extrabold mt-1" style={{ color: 'var(--text)' }}>{members.length}</div>
        </div>
      </div>

      {/* Invite family members */}
      <div className="rounded-2xl p-4 border mb-4" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="family-invite-panel">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus size={17} style={{ color: 'var(--primary)' }} />
          <span className="font-bold" style={{ color: 'var(--text)' }}>{t('fam_invite_t')}</span>
          {invite && <span className="ml-auto text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('fam_slots').replace('{n}', invite.member_count ?? 0).replace('{max}', invite.max_members ?? 5)}</span>}
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{t('fam_invite_b')}</p>
        <div className="rounded-xl p-2 border flex items-center gap-2 mb-2" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }}>
          <input readOnly value={inviteUrl} className="flex-1 bg-transparent text-sm outline-none" style={{ color: 'var(--text)' }} data-testid="family-invite-link" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={copyLink} className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold min-h-[44px] text-sm" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="family-copy-link"><Copy size={16} /> {t('fam_copy')}</button>
          <button onClick={shareLink} className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold min-h-[44px] text-sm border" style={{ borderColor: 'var(--primary)', color: 'var(--primary)', background: 'var(--surface)' }} data-testid="family-share-link"><Share2 size={16} /> {t('fam_share')}</button>
        </div>
        <button onClick={resetLink} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--text-muted)' }} data-testid="family-reset-link"><RefreshCw size={13} /> {t('fam_regen')}</button>

        {/* Roster */}
        <div className="mt-4 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="text-xs font-bold mb-2" style={{ color: 'var(--text)' }}>{t('fam_roster')}</div>
          {(data?.roster || []).length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--text-muted)' }} data-testid="family-roster-empty">{t('fam_roster_empty')}</p>
          ) : (
            <div className="space-y-2" data-testid="family-roster">
              {data.roster.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-xl px-3 py-2 border" style={{ borderColor: 'var(--border)', background: 'var(--surface-alt)' }} data-testid="family-roster-item">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'color-mix(in srgb, var(--primary) 16%, transparent)', color: 'var(--primary)' }}>{(m.member_name || '?').slice(0, 1).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{m.member_name}</div>
                    {m.is_guest && <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('fam_guest_member')}</div>}
                  </div>
                  <button onClick={() => removeMember(m.id)} aria-label={t('fam_remove')} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ color: 'var(--danger)' }} data-testid="family-remove-member"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && !data && <div className="rounded-2xl p-6 border text-center text-sm" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>{t('fam_loading')}</div>}

      {!loading && members.length === 0 && (
        <div className="rounded-2xl p-6 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="family-empty">
          <Users size={28} style={{ color: 'var(--text-muted)' }} className="mx-auto mb-2" />
          <p className="font-semibold" style={{ color: 'var(--text)' }}>{t('fam_empty_t')}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('fam_empty_b').replace('{email}', data?.email || '')}</p>
        </div>
      )}

      <div className="space-y-3">
        {[...active, ...others].map((m) => (
          <div key={m.ride_id} className="rounded-2xl p-4 border" style={{ background: 'var(--surface)', borderColor: m.long_stop ? 'var(--warning)' : 'var(--border)' }} data-testid="family-member-card">
            <div className="flex items-center justify-between">
              <div className="font-bold" style={{ color: 'var(--text)' }}>{m.trusted_contact_name || t('fam_a_ride')}</div>
              {m.status === 'active' ? (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)' }}><span className="w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} /> {t('fam_live')}</span>
              ) : m.safe_confirmed ? (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: 'color-mix(in srgb, var(--success) 14%, transparent)', color: 'var(--success)' }}><CheckCircle2 size={13} /> {t('fam_safe')}</span>
              ) : (
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'var(--surface-alt)', color: 'var(--text-muted)' }}>{t('fam_ended')}</span>
              )}
            </div>
            <div className="text-xs mt-1.5 flex flex-wrap gap-x-3 gap-y-1" style={{ color: 'var(--text-muted)' }}>
              <span>{VEHICLE_LABELS[m.vehicle_type] || m.vehicle_type}</span>
              {m.destination_text && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.destination_text}</span>}
              <span>#{m.public_ride_id}</span>
            </div>
            {m.status === 'active' && (
              <div className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: m.long_stop ? 'var(--warning)' : 'var(--text-muted)' }}>
                <Clock size={12} /> {m.minutes_since_update != null ? t('fam_updated').replace('{n}', m.minutes_since_update) : t('fam_no_gps')}
              </div>
            )}
            {m.long_stop && (
              <div className="mt-2 flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold" style={{ background: 'color-mix(in srgb, var(--warning) 16%, transparent)', color: 'var(--warning)' }} data-testid="family-long-stop">
                <AlertTriangle size={14} /> {t('fam_long_stop')}
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-center mt-4" style={{ color: 'var(--text-muted)' }}>{t('fam_note').replace('{n}', data?.long_stop_minutes ?? 6)}</p>
    </AppShell>
  );
}
